import { useCallback, useEffect, useState } from "react";
import { BarChart3 } from "lucide-react";
import Panel from "../../components/Panel";
import StatCard from "../../components/StatCard";
import { EmptyState, PageHeader } from "../../components/erp";
import { financeAPI } from "../../services/businessErpService";
import { formatCents, titleCase } from "./financeUtils";

export default function FinanceAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await financeAPI.getAnalytics();
      setData(res?.data || null);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const payableSummary = data?.payableSummary || {};
  const receivableSummary = data?.receivableSummary || {};
  const employeeSummary = data?.employeeSummary || {};
  const payableByType = Array.isArray(data?.payableByType) ? data.payableByType : [];
  const receivableByType = Array.isArray(data?.receivableByType) ? data.receivableByType : [];
  const cashflowTrend = Array.isArray(data?.cashflowTrend) ? data.cashflowTrend : [];
  const employeeRollup = Array.isArray(data?.employeeRollup) ? data.employeeRollup : [];

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Finance"
        title="Finance Analytics"
        subtitle="Review operational finance exposure across procurement, payroll, employee balances, and sales-linked receivables."
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard title="Payables Open" value={loading ? "—" : formatCents(payableSummary.open_amount_cents)} icon={BarChart3} />
        <StatCard title="Receivables Open" value={loading ? "—" : formatCents(receivableSummary.open_amount_cents)} icon={BarChart3} />
        <StatCard title="Employee Payables" value={loading ? "—" : formatCents(employeeSummary.open_payable_cents)} icon={BarChart3} />
        <StatCard title="Employee Receivables" value={loading ? "—" : formatCents(employeeSummary.open_receivable_cents)} icon={BarChart3} accent />
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <Panel title="Payables by Type" subtitle="Operational obligation breakdown by source">
          {!loading && !payableByType.length ? (
            <EmptyState icon={BarChart3} title="No payable analytics yet" description="Analytics will populate once procurement receipts or other obligations create finance payables." />
          ) : (
            <div className="space-y-3">
              {payableByType.map((row) => (
                <div key={row.payable_type} className="rounded-xl border border-neutral-200 bg-neutral-50 p-4 dark:border-neutral-800 dark:bg-neutral-900/50">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="text-sm font-medium text-neutral-900 dark:text-neutral-100">{titleCase(row.payable_type)}</div>
                      <div className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">{row.record_count} record(s)</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">{formatCents(row.amount_remaining_cents)}</div>
                      <div className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">Due {formatCents(row.amount_due_cents)}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>

        <Panel title="Receivables by Type" subtitle="Operational collection breakdown by source">
          {!loading && !receivableByType.length ? (
            <EmptyState icon={BarChart3} title="No receivable analytics yet" description="Receivable analytics will populate when sales or recoverable balances create finance receivables." />
          ) : (
            <div className="space-y-3">
              {receivableByType.map((row) => (
                <div key={row.receivable_type} className="rounded-xl border border-neutral-200 bg-neutral-50 p-4 dark:border-neutral-800 dark:bg-neutral-900/50">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="text-sm font-medium text-neutral-900 dark:text-neutral-100">{titleCase(row.receivable_type)}</div>
                      <div className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">{row.record_count} record(s)</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">{formatCents(row.amount_remaining_cents)}</div>
                      <div className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">Due {formatCents(row.amount_due_cents)}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
        <Panel title="Cashflow Movement" subtitle="Recent inflow and outflow recorded through finance settlements">
          {!loading && !cashflowTrend.length ? (
            <div className="text-sm text-neutral-500 dark:text-neutral-400">No disbursement or collection activity yet.</div>
          ) : (
            <div className="overflow-x-auto rounded-2xl border border-neutral-200 dark:border-neutral-800">
              <table className="w-full min-w-[680px] bg-white text-left text-sm dark:bg-neutral-950">
                <thead className="bg-neutral-50 dark:bg-neutral-900/60">
                  <tr className="border-b border-neutral-200 dark:border-neutral-800">
                    <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Month</th>
                    <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Outflow</th>
                    <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Inflow</th>
                    <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Net</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-200 dark:divide-neutral-800">
                  {cashflowTrend.map((row) => {
                    const outflow = Number(row.outflow_cents || 0);
                    const inflow = Number(row.inflow_cents || 0);
                    return (
                      <tr key={row.month_bucket}>
                        <td className="px-3 py-2 font-medium text-neutral-900 dark:text-neutral-100">{row.month_bucket}</td>
                        <td className="px-3 py-2 text-right text-neutral-900 dark:text-neutral-100">{formatCents(outflow)}</td>
                        <td className="px-3 py-2 text-right text-neutral-900 dark:text-neutral-100">{formatCents(inflow)}</td>
                        <td className="px-3 py-2 text-right font-medium text-neutral-900 dark:text-neutral-100">{formatCents(inflow - outflow)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Panel>

        <Panel title="Employee Balance Rollup" subtitle="Open employee finance exposure by direction">
          {!loading && !employeeRollup.length ? (
            <div className="text-sm text-neutral-500 dark:text-neutral-400">No open employee financial balances yet.</div>
          ) : (
            <div className="space-y-3">
              {employeeRollup.map((row) => (
                <div key={row.direction} className="rounded-xl border border-neutral-200 bg-neutral-50 p-4 dark:border-neutral-800 dark:bg-neutral-900/50">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="text-sm font-medium text-neutral-900 dark:text-neutral-100">{titleCase(row.direction)}</div>
                      <div className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">{row.record_count} record(s)</div>
                    </div>
                    <div className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">{formatCents(row.balance_cents)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
}
